import {
  CORRECTION_SECTIONS,
  TECHNICAL_CORRECTION_PRIORITY_LABELS,
  parseDeepLinkTarget,
  validateCorrectionItems,
  type CorrectionSectionDefinition,
  type TechnicalCorrectionItemInput,
  type TechnicalCorrectionPriority,
  type TechnicalCorrectionRequest
} from "../../domain/review/technical-correction.js";
import type { TechnicalDocumentType } from "../../domain/review/technical-document-review.js";

/**
 * Peças de interface da CORREÇÃO POR ITEM, compartilhadas pelos workspaces
 * (Engenharia/PLH) e pelas páginas do Inspetor: o modal "SOLICITAR CORREÇÃO"
 * com vários itens (SEÇÃO / ITEM / OBSERVAÇÃO / PRIORIDADE), os cards
 * "CORREÇÃO SOLICITADA PELO PLH" com [MARCAR COMO CORRIGIDO] e o foco do
 * deep link (scroll + destaque temporário do item).
 */

function esc(value: unknown): string {
  return String(value ?? "").replace(/[&<>"']/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c] || c));
}

export interface CorrectionItemOption { id: string; label: string }

export interface CorrectionModalInfo {
  documentType: TechnicalDocumentType;
  documentNumber: string;
  /** Itens conhecidos por seção (ex.: recomendações REC-001…, pontos P01…). Livre quando ausente. */
  itemsBySection?: Partial<Record<string, CorrectionItemOption[]>>;
  /** Seções disponíveis — padrão: catálogo do tipo. */
  sections?: CorrectionSectionDefinition[];
  /** Título do botão de confirmação. */
  confirmLabel?: string;
}

interface DraftItem { sectionId: string; itemId: string; itemLabel: string; fieldId: string; comment: string; priority: TechnicalCorrectionPriority }

/**
 * <app-correction-request-modal> — SOLICITAR CORREÇÃO por item. Sempre pelo
 * menos um item; seção e observação obrigatórias em cada um.
 */
export class CorrectionRequestModal extends HTMLElement {
  info: CorrectionModalInfo | null = null;
  onConfirm: ((items: TechnicalCorrectionItemInput[]) => Promise<void> | void) | null = null;
  onClosed: (() => void) | null = null;
  private items: DraftItem[] = [];
  private errors: string[] = [];
  private busy = false;

  connectedCallback() {
    if (this.items.length === 0) this.items = [this.blank()];
    this.render();
  }

  private blank(): DraftItem {
    const first = this.sections()[0];
    return { sectionId: first ? first.id : "", itemId: "", itemLabel: "", fieldId: "", comment: "", priority: "NORMAL" };
  }

  private sections(): CorrectionSectionDefinition[] {
    return this.info?.sections || (this.info ? CORRECTION_SECTIONS[this.info.documentType] : []) || [];
  }

  private close() {
    this.remove();
    this.onClosed?.();
  }

  private toInputs(): TechnicalCorrectionItemInput[] {
    return this.items.map(i => {
      const section = this.sections().find(s => s.id === i.sectionId);
      const known = (this.info?.itemsBySection?.[i.sectionId] || []).find(o => o.id === i.itemId);
      const input: TechnicalCorrectionItemInput = { sectionId: i.sectionId, comment: i.comment, priority: i.priority };
      if (i.itemId.trim()) input.itemId = i.itemId.trim();
      if (i.fieldId.trim()) input.fieldId = i.fieldId.trim();
      if (section) input.sectionLabel = section.label;
      const label = known?.label || i.itemLabel.trim();
      if (label) input.itemLabel = label;
      return input;
    });
  }

  private async confirm() {
    if (this.busy) return;
    const inputs = this.toInputs();
    this.errors = validateCorrectionItems(inputs);
    if (this.errors.length > 0) { this.render(); return; }
    this.busy = true; this.render();
    try {
      await this.onConfirm?.(inputs);
      this.remove();
      this.onClosed?.();
    } catch (e: any) {
      this.errors = [e?.safeMessage || e?.message || "Erro ao solicitar correção."];
      this.busy = false;
      this.render();
    }
  }

  render() {
    const info = this.info;
    if (!info) { this.innerHTML = ""; return; }
    const sections = this.sections();
    const priorities = Object.keys(TECHNICAL_CORRECTION_PRIORITY_LABELS) as TechnicalCorrectionPriority[];
    this.innerHTML = `
      <div class="plh-modal-backdrop corr-backdrop">
        <div class="plh-modal corr-modal">
          <div class="plh-modal-head"><span>SOLICITAR CORREÇÃO — ${esc(info.documentNumber)}</span><button class="plh-icon-btn" data-act="close">✕</button></div>
          <div class="plh-modal-body">
            <div class="plh-note">Informe EXATAMENTE o item: seção, item/campo, observação e prioridade. Cada item vira uma pendência própria e o Inspetor responsável recebe uma notificação com navegação direta ao item.</div>
            ${this.items.map((it, index) => {
              const section = sections.find(s => s.id === it.sectionId);
              const options = info.itemsBySection?.[it.sectionId] || [];
              const noun = section?.itemNoun || "Item";
              return `
              <div class="corr-item" data-index="${index}">
                <div class="corr-item-head"><span class="corr-seq">${String(index + 1).padStart(2, "0")}</span>${this.items.length > 1 ? `<button class="plh-btn corr-remove" data-act="remove" data-index="${index}">Remover</button>` : ""}</div>
                <div class="plh-grid">
                  <label class="plh-field"><span>SEÇÃO *</span>
                    <select class="plh-ctl" data-field="sectionId" data-index="${index}">${sections.map(s => `<option value="${esc(s.id)}" ${s.id === it.sectionId ? "selected" : ""}>${esc(s.label)}</option>`).join("")}</select></label>
                  <label class="plh-field"><span>ITEM / CAMPO * (${esc(noun)})</span>
                    ${options.length > 0
                      ? `<select class="plh-ctl" data-field="itemId" data-index="${index}"><option value="">— selecionar —</option>${options.map(o => `<option value="${esc(o.id)}" ${o.id === it.itemId ? "selected" : ""}>${esc(o.label)}</option>`).join("")}<option value="__other" ${it.itemId && !options.some(o => o.id === it.itemId) ? "selected" : ""}>Outro (identificar)</option></select>${it.itemId && !options.some(o => o.id === it.itemId) ? `<input class="plh-ctl" data-field="itemIdFree" data-index="${index}" value="${esc(it.itemId)}" placeholder="Ex.: Ponto P03, Região B">` : ""}`
                      : `<input class="plh-ctl" data-field="itemIdFree" data-index="${index}" value="${esc(it.itemId)}" placeholder="Ex.: ${esc(noun)} 04, REC-003, Região B">`}</label>
                  <label class="plh-field"><span>PRIORIDADE</span>
                    <select class="plh-ctl" data-field="priority" data-index="${index}">${priorities.map(p => `<option value="${p}" ${p === it.priority ? "selected" : ""}>${esc(TECHNICAL_CORRECTION_PRIORITY_LABELS[p].toUpperCase())}</option>`).join("")}</select></label>
                </div>
                <label class="plh-field" style="margin-top:8px;"><span>OBSERVAÇÃO DO ENGENHEIRO *</span>
                  <textarea class="plh-ctl corr-textarea" data-field="comment" data-index="${index}" placeholder="Ex.: Revisar o texto da recomendação. A região indicada no croqui não corresponde à localização informada no checklist.">${esc(it.comment)}</textarea></label>
              </div>`;
            }).join("")}
            <div><button class="plh-btn" data-act="add">+ ADICIONAR ITEM PARA CORREÇÃO</button></div>
            ${this.errors.length > 0 ? `<div class="plh-errors"><strong>Não foi possível enviar:</strong><ul>${this.errors.map(e => `<li>${esc(e)}</li>`).join("")}</ul></div>` : ""}
            <div class="plh-actions">
              <button class="plh-btn" data-act="close" ${this.busy ? "disabled" : ""}>CANCELAR</button>
              <button class="plh-btn primary corr-confirm" data-act="confirm" ${this.busy ? "disabled" : ""}>${esc(info.confirmLabel || "MANDAR PARA CORREÇÃO")}</button>
            </div>
          </div>
        </div>
      </div>`;
    this.querySelectorAll("[data-act='close']").forEach(b => b.addEventListener("click", () => this.close()));
    this.querySelector("[data-act='add']")?.addEventListener("click", () => { this.items.push(this.blank()); this.render(); });
    this.querySelectorAll("[data-act='remove']").forEach(b => b.addEventListener("click", () => { const i = Number((b as HTMLElement).dataset["index"]); this.items.splice(i, 1); this.render(); }));
    this.querySelector("[data-act='confirm']")?.addEventListener("click", () => this.confirm());
    this.querySelectorAll("[data-field]").forEach(el => {
      const input = el as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
      const handler = () => {
        const i = Number(input.dataset["index"]);
        const item = this.items[i];
        if (!item) return;
        const field = input.dataset["field"];
        if (field === "sectionId") { item.sectionId = input.value; item.itemId = ""; item.itemLabel = ""; this.render(); return; }
        if (field === "itemId") {
          if (input.value === "__other") { item.itemId = " "; this.render(); const free = this.querySelector(`[data-field='itemIdFree'][data-index='${i}']`) as HTMLInputElement | null; if (free) { free.value = ""; free.focus(); } return; }
          item.itemId = input.value; item.itemLabel = (input as HTMLSelectElement).selectedOptions?.[0]?.textContent?.trim() || input.value; return;
        }
        if (field === "itemIdFree") { item.itemId = input.value; item.itemLabel = input.value; return; }
        if (field === "priority") { item.priority = input.value as TechnicalCorrectionPriority; return; }
        if (field === "comment") { item.comment = input.value; return; }
      };
      input.addEventListener(input.tagName === "SELECT" ? "change" : "input", handler);
    });
  }
}

if (!customElements.get("app-correction-request-modal")) {
  customElements.define("app-correction-request-modal", CorrectionRequestModal);
}

export function openCorrectionRequestModal(info: CorrectionModalInfo, onConfirm: (items: TechnicalCorrectionItemInput[]) => Promise<void> | void, onClosed?: () => void): CorrectionRequestModal {
  const modal = document.createElement("app-correction-request-modal") as CorrectionRequestModal;
  modal.info = info;
  modal.onConfirm = onConfirm;
  modal.onClosed = onClosed || null;
  document.body.appendChild(modal);
  return modal;
}

/** Cards "CORREÇÃO SOLICITADA PELO PLH" para as pendências abertas do documento (página do Inspetor). */
export function pendingCorrectionsBanner(corrections: readonly TechnicalCorrectionRequest[], canResolve: boolean, highlightId: string | null = null): string {
  const pending = corrections.filter(c => c.status === "PENDENTE");
  if (pending.length === 0) return "";
  const when = (iso: string | null) => (iso ? new Date(iso).toLocaleString("pt-BR") : "—");
  return `
    <div class="corr-banner-list">
      ${pending.map(c => `
        <div class="corr-banner ${c.priority === "CRÍTICA" ? "corr-critical" : c.priority === "ALTA" ? "corr-high" : ""} ${highlightId === c.id ? "deep-link-highlight" : ""}" data-correction-id="${esc(c.id)}" data-section="${esc(c.sectionId)}" data-item="${esc(c.itemId)}">
          <div class="corr-banner-head"><strong>CORREÇÃO SOLICITADA PELO PLH</strong><span class="corr-priority">${esc(c.priority)}</span></div>
          <div class="plh-banner-grid">
            <span><b>Item:</b> ${esc(c.sequence)} · ${esc(c.sectionLabel)}${c.itemLabel ? ` · ${esc(c.itemLabel)}` : ""}${c.fieldId ? ` · campo ${esc(c.fieldId)}` : ""}</span>
            <span><b>Solicitada por:</b> ${esc(c.requestedBy.name)}</span>
            <span style="grid-column: 1 / -1;"><b>Observação:</b> ${esc(c.comment)}</span>
            <span><b>Data:</b> ${esc(when(c.createdAt))}</span>
            <span><b>Documento:</b> ${esc(c.documentNumber)} · Rev. ${esc(c.revision)}</span>
          </div>
          ${canResolve ? `<div class="plh-actions"><a class="plh-btn" href="${esc(c.anchor)}" data-link>Ir ao item</a><button class="plh-btn primary corr-resolve" data-resolve-correction="${esc(c.id)}">MARCAR COMO CORRIGIDO</button></div>` : ""}
        </div>`).join("")}
    </div>`;
}

export interface DeepLinkSelectors {
  section?: (sectionId: string) => string | null;
  item?: (itemId: string, sectionId: string | null) => string | null;
  field?: (fieldId: string) => string | null;
}

/**
 * Depois de renderizar: lê `?section=&item=&field=`, rola até o alvo mais
 * específico que existir e destaca por alguns segundos. Devolve o alvo lido.
 */
export function applyDeepLinkFocus(root: ParentNode & { querySelector: Element["querySelector"] }, selectors: DeepLinkSelectors, search: string = typeof location !== "undefined" ? location.search : ""): ReturnType<typeof parseDeepLinkTarget> {
  const target = parseDeepLinkTarget(search);
  if (!target.sectionId && !target.itemId) return target;
  const candidates: string[] = [];
  if (target.fieldId && selectors.field) { const s = selectors.field(target.fieldId); if (s) candidates.push(s); }
  if (target.itemId && selectors.item) { const s = selectors.item(target.itemId, target.sectionId); if (s) candidates.push(s); }
  if (target.itemId) candidates.push(`[data-item="${cssEscape(target.itemId)}"]`, `[data-rec-id="${cssEscape(target.itemId)}"]`, `[data-correction-id="${cssEscape(target.itemId)}"]`);
  if (target.sectionId && selectors.section) { const s = selectors.section(target.sectionId); if (s) candidates.push(s); }
  if (target.sectionId) candidates.push(`[data-section="${cssEscape(target.sectionId)}"]`);
  setTimeout(() => {
    for (const sel of candidates) {
      let el: Element | null = null;
      try { el = root.querySelector(sel); } catch { el = null; }
      if (!el) continue;
      highlightElement(el as HTMLElement);
      return;
    }
  }, 350);
  return target;
}

export function highlightElement(el: HTMLElement, ms = 6000): void {
  try { el.scrollIntoView({ behavior: "smooth", block: "center" }); } catch { /* ambiente sem scroll */ }
  el.classList.add("deep-link-highlight");
  setTimeout(() => el.classList.remove("deep-link-highlight"), ms);
}

function cssEscape(value: string): string {
  return typeof CSS !== "undefined" && CSS.escape ? CSS.escape(value) : value.replace(/["\\]/g, "\\$&");
}

export const CORRECTION_UI_STYLES = `
  .corr-modal { max-width: 900px; }
  .corr-item { border: 1px solid var(--ia-line, #2B3340); border-radius:8px; padding:12px; background: var(--ia-bg-panel, #0F131A); display:flex; flex-direction:column; gap:6px; }
  .corr-item-head { display:flex; justify-content:space-between; align-items:center; }
  .corr-seq { font-weight:800; color: var(--ia-info-fg, #c4b5fd); font-size:0.9rem; }
  .corr-remove { padding:4px 10px; font-size:0.72rem; }
  .corr-textarea { min-height:72px; resize:vertical; font-family:inherit; }
  .corr-confirm { background:#d97706 !important; border-color:#d97706 !important; }
  .corr-banner-list { display:flex; flex-direction:column; gap:10px; margin-bottom:12px; }
  .corr-banner { padding:12px 14px; border-radius:8px; border:1px solid rgba(245,158,11,0.55); background: rgba(245,158,11,0.12); color: var(--ia-warning-fg, #fde68a); font-size:0.82rem; display:flex; flex-direction:column; gap:6px; }
  .corr-banner.corr-high { border-color: rgba(249,115,22,0.7); }
  .corr-banner.corr-critical { border-color: rgba(239,68,68,0.8); background: rgba(239,68,68,0.12); color: var(--ia-danger-fg, #fecaca); }
  .corr-banner-head { display:flex; justify-content:space-between; align-items:center; }
  .corr-priority { font-size:0.68rem; font-weight:800; padding:2px 8px; border-radius:10px; background: rgba(0,0,0,0.25); }
  .corr-banner .plh-actions { justify-content:flex-end; margin-top:4px; }
  .corr-banner a.plh-btn { text-decoration:none; }
  .corr-resolve { background:#059669 !important; border-color:#059669 !important; }
  .deep-link-highlight { outline: 3px solid #f59e0b !important; outline-offset: 3px; box-shadow: 0 0 0 6px rgba(245,158,11,0.25) !important; transition: box-shadow 0.4s ease; animation: deepLinkPulse 1.2s ease-in-out 3; }
  @keyframes deepLinkPulse { 0%, 100% { box-shadow: 0 0 0 6px rgba(245,158,11,0.25); } 50% { box-shadow: 0 0 0 12px rgba(245,158,11,0.05); } }
`;
